// web-frontend/src/shared/config/tenant.js
// Multi-tenant runtime config.

const DEFAULT_FEATURES = {
  auth: true,
  users: true,
  companies: true,
  events: true,
  i18n: true,
  matching: true,
  matching_simulator: true,
  donations: true,
  carbon: false, // 🔥 [2026-06-26] 탄소포인트 우선 미사용 — 전 테넌트에서 숨김
  payments: true,
  meetings: true,
  chats: true,
  surveys: true,
  broadcast: true,
  reports: true,
  booth_map: true,
  dashboard: true,
  audit_logs: true,
  content_library: true,
  translations: true,
  ai_curator: true,
  vector_search: true,
  materials: true,
  chatbot: true,
  sms: true,
  social_auth: true,
  notifications: true,
  bookmarks: true,
};

const DSK_2026_BRAND = {
  primary: '#2563EB',
  primaryLight: '#3B82F6',
  primaryDark: '#1D4ED8',
  primaryGlow: 'rgba(37, 99, 235, 0.3)',
  primaryPale: 'rgba(37, 99, 235, 0.1)',
  secondary: '#E51C75',
  secondaryLight: '#F04FA0',
  secondaryDark: '#C01670',
  secondaryGlow: 'rgba(229, 28, 117, 0.3)',
  secondaryPale: 'rgba(229, 28, 117, 0.1)',
  tertiary: '#E51C75',
  tertiaryLight: '#F04FA0',
  tertiaryDark: '#C01670',
  tertiaryGlow: 'rgba(229, 28, 117, 0.3)',
  tertiaryPale: 'rgba(229, 28, 117, 0.1)',
  softSky: '#F8FBFF',
  primaryDarkMode: '#60A5FA',
  secondaryDarkMode: '#F472B6',
  tertiaryDarkMode: '#F472B6',
};

const DSK_2027_BRAND = {
  primary: '#052243',
  primaryLight: '#1A3A5E',
  primaryDark: '#031530',
  primaryGlow: 'rgba(5, 34, 67, 0.18)',
  primaryPale: 'rgba(5, 34, 67, 0.08)',
  splashPrimaryGlow: 'rgba(5, 34, 67, 0.12)',

  secondary: '#fbba21',
  secondaryLight: '#FCC94D',
  secondaryDark: '#C99818',
  secondaryGlow: 'rgba(251, 186, 33, 0.22)',
  secondaryPale: 'rgba(251, 186, 33, 0.1)',
  splashSecondaryGlow: 'rgba(251, 186, 33, 0.16)',

  tertiary: '#ff3875',
  tertiaryLight: '#FF6395',
  tertiaryDark: '#CC2D5E',
  tertiaryGlow: 'rgba(255, 56, 117, 0.18)',
  tertiaryPale: 'rgba(255, 56, 117, 0.1)',
  splashTertiaryGlow: 'rgba(255, 56, 117, 0.05)',

  softSky: '#F0F4FA',
  splashLogoShadow: 'rgba(5, 34, 67, 0.12)',
  primaryDarkMode: '#4F7BB8',
  secondaryDarkMode: '#FCC94D',
  tertiaryDarkMode: '#FF6395',
};

const DEFAULT_ASSETS = {
  logo: '/assets/logo.png',
  logoLight: '/assets/logo.png',
  drone: '/assets/images/img_drone.png',
  hero: '/assets/images/img_drone.png',
  boothMap: '/assets/map/booth.png',
  boothMapMobile: '/assets/map/booth_mobile.json',
  favicon: '/favicon.ico',
  ui: {},
};

const DSK2027_ASSETS = {
  logo: '/assets/tenants/dsk2027/logo.png',
  logoLight: '/assets/tenants/dsk2027/logo.png',
  drone: '/assets/tenants/dsk2027/drone.png',
  hero: '/assets/tenants/dsk2027/hero-expo.png',
  boothMap: '/assets/tenants/dsk2027/booth-map.png',
  boothMapMobile: '/assets/tenants/dsk2027/booth-map-mobile.json',
  favicon: '/assets/tenants/dsk2027/favicon.ico',
  ui: {
    matching: '/assets/tenants/dsk2027/ui/feature-ai-matching.png',
    survey: '/assets/tenants/dsk2027/ui/feature-survey.png',
    meeting: '/assets/tenants/dsk2027/ui/feature-meeting-request.png',
    meetingAccept: '/assets/tenants/dsk2027/ui/feature-meeting-accept.png',
    meetingChange: '/assets/tenants/dsk2027/ui/feature-meeting-change.png',
    meetingReject: '/assets/tenants/dsk2027/ui/feature-meeting-reject.png',
    navigation: '/assets/tenants/dsk2027/ui/feature-navigation.png',
    boothMap: '/assets/tenants/dsk2027/ui/feature-booth-map.png',
    chat: '/assets/tenants/dsk2027/ui/feature-chat.png',
    companies: '/assets/tenants/dsk2027/ui/feature-companies.png',
    events: '/assets/tenants/dsk2027/ui/feature-events.png',
    carbon: '/assets/tenants/dsk2027/ui/feature-carbon.png',
    company: '/assets/tenants/dsk2027/ui/user-company.png',
    buyer: '/assets/tenants/dsk2027/ui/user-buyer.png',
    investor: '/assets/tenants/dsk2027/ui/user-investor.png',
    businessVisitor: '/assets/tenants/dsk2027/ui/user-business-visitor.png',
    visitor: '/assets/tenants/dsk2027/ui/user-visitor.png',
    guest: '/assets/tenants/dsk2027/ui/user-guest.png',
    adminDashboard: '/assets/tenants/dsk2027/ui/admin-dashboard.png',
    adminUsers: '/assets/tenants/dsk2027/ui/admin-users.png',
    adminApprovals: '/assets/tenants/dsk2027/ui/admin-approvals.png',
    adminMeetings: '/assets/tenants/dsk2027/ui/admin-meetings.png',
    adminMatching: '/assets/tenants/dsk2027/ui/admin-matching.png',
    adminSurveys: '/assets/tenants/dsk2027/ui/admin-surveys.png',
    ec2: '/assets/tenants/dsk2027/ui/ops-ec2.png',
    tenant: '/assets/tenants/dsk2027/ui/ops-tenant.png',
    nginx: '/assets/tenants/dsk2027/ui/ops-nginx.png',
    database: '/assets/tenants/dsk2027/ui/ops-database.png',
    bexco: '/assets/tenants/dsk2027/ui/event-bexco.png',
    halls: '/assets/tenants/dsk2027/ui/event-halls.png',
    drone: '/assets/tenants/dsk2027/ui/event-drone.png',
    mobility: '/assets/tenants/dsk2027/ui/event-mobility.png',
    pending: '/assets/tenants/dsk2027/ui/status-pending.png',
    approved: '/assets/tenants/dsk2027/ui/status-approved.png',
    declined: '/assets/tenants/dsk2027/ui/status-declined.png',
    reschedule: '/assets/tenants/dsk2027/ui/status-reschedule.png',
    curation: {
      map: '/assets/tenants/dsk2027/curation/spc-map.png',
      companies: '/assets/tenants/dsk2027/curation/spc-companies.png',
      aiGuide: '/assets/tenants/dsk2027/curation/spc-ai-guide.png',
      events: '/assets/tenants/dsk2027/curation/spc-events.png',
      businessPass: '/assets/tenants/dsk2027/curation/spc-business-pass.png',
      meeting: '/assets/tenants/dsk2027/curation/spc-meeting.png',
      survey: '/assets/tenants/dsk2027/curation/spc-survey.png',
    },
    curation2d: {
      map: '/assets/tenants/dsk2027/curation/spc-2d-map.png',
      companies: '/assets/tenants/dsk2027/curation/spc-2d-companies.png',
      aiGuide: '/assets/tenants/dsk2027/curation/spc-2d-ai-guide.png',
      events: '/assets/tenants/dsk2027/curation/spc-2d-events.png',
      businessPass: '/assets/tenants/dsk2027/curation/spc-2d-business-pass.png',
      meeting: '/assets/tenants/dsk2027/curation/spc-2d-meeting.png',
      survey: '/assets/tenants/dsk2027/curation/spc-2d-survey.png',
    },
  },
};

const TENANTS = {
  DSK: {
    id: 'DSK',
    package: 'FULL', // 판매 패키지 (FULL | BLEND | SPC_ONLY) — docs/packaging.md
    eventName: 'DSK 2026',
    eventNameMultiline: 'DRONE SHOW\nKOREA',
    fullName: 'Drone Show Korea',
    acronym: 'DSK',
    description: {
      ko: 'Drone Show Korea',
      en: 'Drone Show Korea',
      ja: 'Drone Show Korea',
      zh: 'Drone Show Korea',
    },
    venue: {
      name: 'BEXCO',
      fullName: 'BEXCO Exhibition Hall 1',
      fullNameLocalized: {
        ko: 'BEXCO Exhibition Hall 1',
        en: 'BEXCO Exhibition Hall 1',
        ja: 'BEXCO Exhibition Hall 1',
        zh: 'BEXCO Exhibition Hall 1',
      },
      address: '55 APEC-ro, Haeundae-gu, Busan',
      city: 'Busan',
      country: 'South Korea',
      coords: { lat: 35.1691, lon: 129.1363 },
    },
    dates: { start: '2026-02-25', end: '2026-02-28' },
    timezone: 'Asia/Seoul', // IANA 존 — 사용자 화면 시간 표시 기준 (utils/dateTime.js)
    tzLabel: 'KST',
    defaultPhoneCountryCode: '82',
    defaultLanguage: 'ko',
    homeCountry: {
      code: 'KR',
      flag: 'KR',
      labels: { ko: 'South Korea', en: 'South Korea', ja: 'South Korea', zh: 'South Korea' },
    },
    regionDataset: 'KR',
    brand: DSK_2026_BRAND,
    assets: DEFAULT_ASSETS,
    ci: {
      tagline: { ko: 'Carbon Neutral Exhibition', en: 'Carbon Neutral Exhibition' },
      accentColor: '#F59E0B',
    },
    features: DEFAULT_FEATURES,
  },

  AP: {
    id: 'AP',
    package: 'FULL', // ESG(donations/carbon) override 로 제외
    eventName: 'AI Power +',
    eventNameMultiline: 'AI POWER +',
    fullName: 'AI Power +',
    acronym: 'AIP',
    description: {
      ko: 'AI Power +',
      en: 'AI Power +',
      ja: 'AI Power +',
      zh: 'AI Power +',
    },
    venue: {
      name: 'HKCEC',
      fullName: 'Hong Kong Convention and Exhibition Centre',
      fullNameLocalized: {
        ko: 'Hong Kong Convention and Exhibition Centre',
        en: 'Hong Kong Convention and Exhibition Centre',
        ja: 'Hong Kong Convention and Exhibition Centre',
        zh: 'Hong Kong Convention and Exhibition Centre',
      },
      address: '1 Expo Drive, Wan Chai, Hong Kong',
      city: 'Hong Kong',
      country: 'Hong Kong',
      coords: { lat: 22.2825, lon: 114.1731 },
    },
    dates: { start: '2026-06-04', end: '2026-06-05' },
    timezone: 'Asia/Hong_Kong',
    tzLabel: 'HKT',
    defaultPhoneCountryCode: '852',
    defaultLanguage: 'en',
    homeCountry: {
      code: 'HK',
      flag: 'HK',
      labels: { ko: 'Hong Kong', en: 'Hong Kong', ja: 'Hong Kong', zh: 'Hong Kong' },
    },
    regionDataset: 'HK',
    brand: {
      primary: '#1E90FF',
      primaryLight: '#4169E1',
      primaryDark: '#1C5CC0',
      primaryGlow: 'rgba(30, 144, 255, 0.3)',
      primaryPale: 'rgba(30, 144, 255, 0.1)',
      secondary: '#00D084',
      secondaryLight: '#2FD899',
      secondaryDark: '#00A86B',
      secondaryGlow: 'rgba(0, 208, 132, 0.3)',
      secondaryPale: 'rgba(0, 208, 132, 0.1)',
      tertiary: '#FF6B6B',
      tertiaryLight: '#FF8E8E',
      tertiaryDark: '#D94A4A',
      tertiaryGlow: 'rgba(255, 107, 107, 0.3)',
      tertiaryPale: 'rgba(255, 107, 107, 0.1)',
      softSky: '#F0F8FF',
      primaryDarkMode: '#6BA3FF',
      secondaryDarkMode: '#3CFF9D',
      tertiaryDarkMode: '#FF8E8E',
    },
    assets: DEFAULT_ASSETS,
    ci: {
      tagline: { ko: 'AI Power+ Exhibition', en: 'AI Power+ Exhibition' },
      accentColor: '#FF6B6B',
    },
    features: { ...DEFAULT_FEATURES, donations: false, carbon: false },
  },

  DSK2027: {
    id: 'DSK2027',
    package: 'FULL',
    eventName: 'DSK 2027',
    eventNameMultiline: 'DRONE SHOW\nKOREA 2027',
    fullName: 'Drone Show Korea 2027',
    acronym: 'DSK',
    description: {
      ko: '드론쇼 코리아 2027 - The Global Hub Where Innovation Takes Off',
      en: 'Drone Show Korea 2027 - The Global Hub Where Innovation Takes Off',
      ja: 'Drone Show Korea 2027',
      zh: 'Drone Show Korea 2027',
    },
    venue: {
      name: 'BEXCO',
      fullName: 'BEXCO Exhibition Hall 1 (Halls 1, 2, 3) / Convention Hall',
      fullNameLocalized: {
        ko: 'BEXCO 제1전시장 1,2,3홀 / 컨벤션홀',
        en: 'BEXCO Exhibition Hall 1 (Halls 1, 2, 3) / Convention Hall',
        ja: 'BEXCO Exhibition Hall 1 (Halls 1, 2, 3) / Convention Hall',
        zh: 'BEXCO Exhibition Hall 1 (Halls 1, 2, 3) / Convention Hall',
      },
      address: '55 APEC-ro, Haeundae-gu, Busan',
      city: 'Busan',
      country: 'South Korea',
      coords: { lat: 35.1691, lon: 129.1363 },
      halls: [
        { code: 'EXHIBITION_1_HALL_1', name: 'Hall 1', type: 'exhibition' },
        { code: 'EXHIBITION_1_HALL_2', name: 'Hall 2', type: 'exhibition' },
        { code: 'EXHIBITION_1_HALL_3', name: 'Hall 3', type: 'exhibition' },
        { code: 'CONVENTION_HALL', name: 'Convention Hall', type: 'convention' },
      ],
    },
    dates: { start: '2027-02-24', end: '2027-02-26' },
    timezone: 'Asia/Seoul',
    tzLabel: 'KST',
    defaultPhoneCountryCode: '82',
    defaultLanguage: 'ko',
    homeCountry: {
      code: 'KR',
      flag: 'KR',
      labels: { ko: '대한민국', en: 'South Korea', ja: 'South Korea', zh: 'South Korea' },
    },
    regionDataset: 'KR',
    brand: DSK_2027_BRAND,
    assets: DSK2027_ASSETS,
    ci: {
      tagline: {
        ko: 'The Global Hub Where Innovation Takes Off',
        en: 'The Global Hub Where Innovation Takes Off',
      },
      slogan: {
        ko: '드론, 항공우주, 미래 모빌리티까지 첨단 기술의 혁신 허브',
        en: 'Advanced technology innovation hub spanning drones, aerospace, and future mobility',
      },
      accentColor: '#ff3875',
      organizers: [
        { ko: '산업통상부', en: 'Ministry of Trade, Industry and Energy' },
        { ko: '국토교통부', en: 'Ministry of Land, Infrastructure and Transport' },
        { ko: '국방부', en: 'Ministry of National Defense' },
        { ko: '우주항공청', en: 'Korea AeroSpace Administration' },
        { ko: '부산광역시', en: 'Busan Metropolitan City' },
      ],
      coOrganizers: [
        { ko: 'BEXCO', en: 'BEXCO' },
        { ko: '한국무인기시스템협회', en: 'Korea Unmanned Vehicle System Association' },
      ],
    },
    features: DEFAULT_FEATURES,
  },
};

function normalizeTenantId(id) {
  if (!id) return null;
  const upper = String(id).toUpperCase();
  return TENANTS[upper] ? upper : null;
}

function fromHostname() {
  if (typeof window === 'undefined' || !window.location) return null;

  const host = String(window.location.hostname || '').toLowerCase();
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return null;
  }

  const segments = host.split('.');
  for (const segment of segments) {
    const matched = normalizeTenantId(segment);
    if (matched) return matched;
  }

  return null;
}

function resolveActiveTenantId() {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const fromEnv = normalizeTenantId(import.meta.env.VITE_TENANT);
      if (fromEnv) return fromEnv;
    }
  } catch (_error) {
    // import.meta can be unavailable in non-Vite tooling.
  }

  const hostTenant = fromHostname();
  if (hostTenant) return hostTenant;

  if (typeof window !== 'undefined' && window.__DSK_TENANT__) {
    const windowTenant = normalizeTenantId(window.__DSK_TENANT__);
    if (windowTenant) return windowTenant;
  }

  return 'DSK';
}

const ACTIVE_TENANT_ID = resolveActiveTenantId();
const ACTIVE_TENANT = TENANTS[ACTIVE_TENANT_ID] || TENANTS.DSK;

export function getTenant() {
  return ACTIVE_TENANT;
}

export function getTenantId() {
  return ACTIVE_TENANT.id;
}

export function getTenantPackage() {
  return ACTIVE_TENANT.package || 'FULL';
}

export function isTenant(id) {
  return ACTIVE_TENANT.id === String(id || '').toUpperCase();
}

export function getEventName() {
  return ACTIVE_TENANT.eventName;
}

export function getEventNameMultiline() {
  return ACTIVE_TENANT.eventNameMultiline;
}

export function getTimezone() {
  return ACTIVE_TENANT.timezone || TENANTS.DSK.timezone;
}

export function getTimezoneLabel() {
  return ACTIVE_TENANT.tzLabel || TENANTS.DSK.tzLabel;
}

export function getEventDescription(lang = 'en') {
  return ACTIVE_TENANT.description?.[lang] || ACTIVE_TENANT.description?.en || '';
}

export function getVenue() {
  return ACTIVE_TENANT.venue;
}

export function getVenueName() {
  return ACTIVE_TENANT.venue?.name || '';
}

export function getVenueFullName(lang) {
  const venue = ACTIVE_TENANT.venue || {};
  if (lang && venue.fullNameLocalized?.[lang]) {
    return venue.fullNameLocalized[lang];
  }
  return venue.fullName || venue.name || '';
}

export function getVenueCoords() {
  return ACTIVE_TENANT.venue?.coords;
}

export function getDefaultPhoneCountryCode() {
  return ACTIVE_TENANT.defaultPhoneCountryCode;
}

export function getHomeCountry() {
  return ACTIVE_TENANT.homeCountry;
}

export function getRegionDataset() {
  return ACTIVE_TENANT.regionDataset;
}

export function getBrand() {
  return { ...DSK_2026_BRAND, ...(ACTIVE_TENANT.brand || {}) };
}

export function getBrandColor(key = 'primary') {
  return getBrand()[key];
}

export function getAssets() {
  return { ...DEFAULT_ASSETS, ...(ACTIVE_TENANT.assets || {}) };
}

export function getTenantLogo() {
  return getAssets().logo;
}

export function getTagline(lang = 'en') {
  const tagline = ACTIVE_TENANT.ci?.tagline;
  if (!tagline) return '';
  if (typeof tagline === 'string') return tagline;
  return tagline[lang] || tagline.en || '';
}

export function getDefaultLanguage() {
  return ACTIVE_TENANT.defaultLanguage || 'en';
}

// =============================================================================
// 가입 인증 정책 (2026-07-31) — 자가 가입 시 강제할 인증 채널.
// backend/config/tenants.py 의 SIGNUP_VERIFICATION_DEFAULT / TENANT_SIGNUP_VERIFICATION 와
// lockstep. 테넌트별로 다르게 하려면 TENANTS[id].signupVerification 에 override 를 넣고
// backend 에도 동일하게 반영할 것 (예: { sms: false }  → 이메일만 필수).
// =============================================================================
const DEFAULT_SIGNUP_VERIFICATION = { sms: true, email: true };

export function getSignupVerification() {
  return { ...DEFAULT_SIGNUP_VERIFICATION, ...(ACTIVE_TENANT.signupVerification || {}) };
}

export function getFeatures() {
  return ACTIVE_TENANT.features || {};
}

export function isFeatureEnabled(name) {
  return getFeatures()[name] === true;
}

export function getFeature(name) {
  return isFeatureEnabled(name);
}
